import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronDown, ChevronUp, Percent, Moon, Calendar, Euro, X } from 'lucide-react';
import RoomCalendarPicker from './RoomCalendarPicker';

const API = process.env.REACT_APP_BACKEND_URL;

const RoomRatesAccordion = ({ 
  room, 
  property, 
  checkIn, 
  checkOut, 
  adults, 
  children,
  isExpanded,
  onToggle,
  onDatesChange
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Calculate number of nights
  const calculateNights = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  const nights = calculateNights(checkIn, checkOut);

  // Fetch rates when accordion is expanded
  useEffect(() => {
    if (isExpanded && rates.length === 0) {
      fetchRates();
    }
  }, [isExpanded]);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/rates/room/${room.roomId}`, {
        params: { check_in: checkIn, check_out: checkOut }
      });
      setRates(response.data);
    } catch (error) {
      console.error('Error fetching rates:', error);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  // Get translated text with fallback
  const getLocalizedText = (rate, field) => {
    const lang = i18n.language;
    return rate[`${field}_${lang}`] || rate[`${field}_en`] || rate[`${field}_hr`] || '';
  };

  // Check if a rate is a "surcharge rate" (seasonal/thematic with fixed surcharge and date limits)
  const isSurchargeRate = (rate) => {
    return (rate.fixed_surcharge > 0) && (rate.start_date || rate.end_date);
  };

  // Check if a rate is a promotional rate (has color or is surcharge rate with dates)
  const isPromotionalRate = (rate) => {
    return rate.color || ((rate.start_date || rate.end_date) && rate.fixed_surcharge > 0);
  };

  // Check if booking dates overlap with rate's valid period
  const bookingOverlapsWithRate = (rate) => {
    if (!rate.start_date && !rate.end_date) return true; // No date restrictions
    
    const rateStart = rate.start_date || '1900-01-01';
    const rateEnd = rate.end_date || '2100-12-31';
    
    // Overlap: booking starts before rate ends AND booking ends after rate starts
    return checkIn <= rateEnd && checkOut > rateStart;
  };

  // Filter and categorize rates
  // Returns: { discountRates: [], surchargeRates: [], upcomingPromotions: [], bestDiscountRate: null }
  const getCategorizedRates = () => {
    if (!rates || rates.length === 0) return { discountRates: [], surchargeRates: [], upcomingPromotions: [], bestDiscountRate: null };

    const discountRates = [];
    const surchargeRates = [];
    const upcomingPromotions = [];

    rates.forEach(rate => {
      const overlaps = bookingOverlapsWithRate(rate);
      
      // If it's a promotional rate but dates don't overlap, add to upcoming promotions
      if (!overlaps && isPromotionalRate(rate)) {
        upcomingPromotions.push({ 
          ...rate, 
          canBook: false, 
          isQualified: false, 
          isSurcharge: isSurchargeRate(rate),
          isUpcoming: true 
        });
        return;
      }
      
      // Skip non-overlapping non-promotional rates
      if (!overlaps) return;

      if (isSurchargeRate(rate)) {
        // This is a surcharge rate (e.g., Valentine's)
        surchargeRates.push({ ...rate, canBook: true, isQualified: true, isSurcharge: true, isUpcoming: false });
      } else {
        // This is a regular discount rate
        const meetsMin = nights >= rate.min_nights;
        const meetsMax = !rate.max_nights || nights <= rate.max_nights;
        const isQualified = meetsMin && meetsMax;
        
        discountRates.push({
          ...rate,
          canBook: isQualified,
          isQualified: isQualified,
          isSurcharge: false,
          isUpcoming: false
        });
      }
    });

    // Sort discount rates by min_nights
    discountRates.sort((a, b) => a.min_nights - b.min_nights);

    // Find the best discount rate user qualifies for
    const qualifyingDiscountRates = discountRates.filter(r => r.isQualified);
    let bestDiscountRate = null;
    
    if (qualifyingDiscountRates.length > 0) {
      bestDiscountRate = qualifyingDiscountRates.reduce((best, current) => 
        current.discount_percent > best.discount_percent ? current : best
      );
    }

    // Filter discount rates: show only best qualifying + higher tier upgrades
    let filteredDiscountRates = [];
    if (bestDiscountRate) {
      // Add the best qualifying rate
      filteredDiscountRates.push(bestDiscountRate);
      
      // Add rates with higher min_nights (potential upgrades)
      discountRates.forEach(rate => {
        if (rate.min_nights > nights && rate.discount_percent > bestDiscountRate.discount_percent) {
          filteredDiscountRates.push(rate);
        }
      });
    } else {
      // User doesn't qualify for any discount rate - show all
      filteredDiscountRates = discountRates;
    }

    return {
      discountRates: filteredDiscountRates,
      surchargeRates,
      upcomingPromotions,
      bestDiscountRate
    };
  };

  // Get applicable rates for display (combines discount + surcharge + upcoming promotions)
  const getApplicableRates = () => {
    const { discountRates, surchargeRates, upcomingPromotions, bestDiscountRate } = getCategorizedRates();
    
    // Combine: show discount rates first, then surcharge rates, then upcoming promotions
    return [...discountRates, ...surchargeRates, ...upcomingPromotions];
  };

  // Handle booking with rate
  const handleBook = (rate, combinedWithSurcharge = null) => {
    // Calculate the total price INCLUDING extra guests
    // Discount applies to TOTAL (base + extra guests)
    const totalPricePerNight = basePricePerNight + extraGuestPerNight;
    const userTotal = totalPricePerNight * nights;
    
    // Apply discount from the rate (or from bestDiscountRate if this is a surcharge rate)
    let discountPercent = rate.discount_percent;
    let discountRateName = getLocalizedText(rate, 'name');
    let surchargeAmount = rate.fixed_surcharge || 0;
    let surchargeRateName = '';
    
    if (rate.isSurcharge && combinedWithSurcharge) {
      // This is a surcharge rate being combined with a discount rate
      discountPercent = combinedWithSurcharge.discount_percent;
      discountRateName = getLocalizedText(combinedWithSurcharge, 'name');
      surchargeAmount = rate.fixed_surcharge || 0;
      surchargeRateName = getLocalizedText(rate, 'name');
    } else if (rate.isSurcharge) {
      // Surcharge rate without discount - apply best available discount
      const { bestDiscountRate } = getCategorizedRates();
      if (bestDiscountRate) {
        discountPercent = bestDiscountRate.discount_percent;
        discountRateName = getLocalizedText(bestDiscountRate, 'name');
        surchargeRateName = getLocalizedText(rate, 'name');
      } else {
        discountPercent = 0;
        discountRateName = '';
        surchargeRateName = getLocalizedText(rate, 'name');
      }
    }
    
    // Apply discount to TOTAL price (base + extra guests)
    const discountAmount = (userTotal * discountPercent) / 100;
    const discountedPrice = userTotal - discountAmount + surchargeAmount;
    
    // Build rate name for display
    let finalRateName = discountRateName;
    if (surchargeRateName) {
      finalRateName = discountRateName ? `${discountRateName} + ${surchargeRateName}` : surchargeRateName;
    }
    
    // Include extra guest info in URL params
    const extraGuestPrice = room.extra_guest_price || 0;
    const extraGuestsCount = room.extra_guests_count || 0;
    const extraGuestTotalForBooking = extraGuestPrice * extraGuestsCount * nights;
    
    navigate(`/booking?propertyId=${property.id || property.propId || property.property_id}&roomId=${room.roomId}&checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&propertyName=${encodeURIComponent(property.property_name)}&roomName=${encodeURIComponent(room.name)}&price=${discountedPrice}&rateId=${rate.id}&rateName=${encodeURIComponent(finalRateName)}&rateDiscount=${discountPercent}&rateSurcharge=${surchargeAmount}&extraGuestPrice=${extraGuestPrice}&extraGuestsCount=${extraGuestsCount}&extraGuestTotal=${extraGuestTotalForBooking}`);
  };

  // Handle date adjustment - just open the picker
  const handleAdjustDates = () => {
    setShowDatePicker(true);
  };

  const applicableRates = getApplicableRates();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  // Calculate price per night from room's BASE price (without extra guests)
  // Extra guest charges will be added separately
  const basePriceTotal = room.base_price || room.price;
  const basePricePerNight = nights > 0 ? basePriceTotal / nights : basePriceTotal;
  const extraGuestTotal = room.extra_guest_total || 0;
  const extraGuestPerNight = nights > 0 ? extraGuestTotal / nights : 0;

  // Calculate price for a specific rate
  // Discount applies to TOTAL price (base + extra guests)
  // Formula: (base + extra_guests) * (1 - discount%) + surcharge
  const calculateRatePrice = (rate) => {
    const nightsToUse = rate.canBook ? nights : rate.min_nights;
    // Calculate base price for the nights
    const baseTotal = basePricePerNight * nightsToUse;
    // Extra guests for the nights being calculated
    const extraGuestForNights = (room.extra_guest_price || 0) * (room.extra_guests_count || 0) * nightsToUse;
    // Total before discount (base + extra guests)
    const totalBeforeDiscount = baseTotal + extraGuestForNights;
    
    let discountPercent = rate.discount_percent;
    let surcharge = rate.fixed_surcharge || 0;
    
    // If this is a surcharge rate, apply the best available discount rate first
    if (rate.isSurcharge) {
      const { bestDiscountRate } = getCategorizedRates();
      if (bestDiscountRate) {
        discountPercent = bestDiscountRate.discount_percent;
      } else {
        discountPercent = 0;
      }
    }
    
    // Apply discount to TOTAL price (base + extra guests)
    const discountAmount = (totalBeforeDiscount * discountPercent) / 100;
    const discountedTotal = totalBeforeDiscount - discountAmount;
    // Final price = discounted total + surcharge
    const finalPrice = discountedTotal + surcharge;
    
    return {
      total: finalPrice,
      nights: nightsToUse,
      perNight: finalPrice / nightsToUse,
      baseDiscount: discountPercent,
      surcharge: surcharge,
      extraGuestTotal: extraGuestForNights
    };
  };

  return (
    <div className="border-t border-gray-100">
      {/* Accordion Content - NO TOGGLE BUTTON, controlled by parent */}
      {isExpanded && (
        <div className="bg-white border-t border-gray-100">
          {loading ? (
            <div className="text-center py-4 text-gray-500">
              {t('common.loading')}
            </div>
          ) : applicableRates.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              {t('rates.noRatesAvailable')}
            </div>
          ) : (
            <div className="space-y-3">
              <h4 className="my-5 font-semibold text-sm text-gray-700 mb-3">
                {t('rates.availableRates')}
              </h4>
              
              {applicableRates.map((rate) => {
                const ratePrice = calculateRatePrice(rate);
                const rateName = getLocalizedText(rate, 'name');
                const shortDesc = getLocalizedText(rate, 'short_desc');
                const { bestDiscountRate } = getCategorizedRates();
                
                // Get dynamic color for promotional rates
                const rateColor = rate.color || (rate.isSurcharge ? '#ec4899' : null);
                const isPromotional = rate.isSurcharge || rate.color;
                
                // For surcharge rates, show combined name if discount applies
                let displayName = rateName || 'Standard Rate';
                if (rate.isSurcharge && bestDiscountRate && bestDiscountRate.discount_percent > 0 && !rate.isUpcoming) {
                  const discountRateName = getLocalizedText(bestDiscountRate, 'name');
                  displayName = `${discountRateName} + ${rateName}`;
                }

                // Generate styles based on rate type
                const getCardStyles = () => {
                  if (rate.isUpcoming) {
                    // Upcoming promotion - grayed out but with subtle color hint
                    return {
                      className: 'border-2 border-dashed opacity-75',
                      style: { 
                        borderColor: rateColor || '#9ca3af',
                        backgroundColor: `${rateColor || '#9ca3af'}10`
                      }
                    };
                  }
                  if (isPromotional && rateColor) {
                    // Active promotional rate with custom color
                    return {
                      className: 'border-2',
                      style: { 
                        borderColor: rateColor,
                        background: `linear-gradient(to right, ${rateColor}15, ${rateColor}08)`
                      }
                    };
                  }
                  if (rate.isQualified) {
                    return {
                      className: 'border-2 border-[var(--color-primary)] bg-[var(--color-primary)]/5',
                      style: {}
                    };
                  }
                  return {
                    className: 'border-2 border-gray-200 bg-gray-50',
                    style: {}
                  };
                };

                const cardStyles = getCardStyles();

                return (
                  <div 
                    key={rate.id}
                    className={`p-4 rounded-lg transition-all ${cardStyles.className}`}
                    style={cardStyles.style}
                    data-testid={`rate-card-${rate.id}`}
                  >
                    {/* Upcoming Promotion Banner */}
                    {rate.isUpcoming && (
                      <div 
                        className="text-xs font-semibold mb-3 px-2 py-1 rounded inline-block text-white"
                        style={{ backgroundColor: rateColor || '#6b7280' }}
                      >
                        {t('rates.promotionLabel') || 'Promocija'}
                      </div>
                    )}
                    
                    {/* Main Content Container - stacks vertically on mobile (<500px) */}
                    <div className="flex flex-col min-[500px]:flex-row min-[500px]:justify-between gap-3 min-[500px]:gap-4">
                      {/* Left Side - Rate Info */}
                      <div className="flex-1 min-w-0">
                        {/* Title */}
                        <h5 className={`font-semibold text-base leading-snug mb-2 ${rate.isUpcoming ? 'text-gray-600' : ''}`}>
                          {displayName}
                        </h5>
                        
                        {/* Badges Row */}
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {rate.isSurcharge || (isPromotional && rate.fixed_surcharge > 0) ? (
                            <>
                              <Badge 
                                className="text-white text-xs px-2 py-0.5"
                                style={{ backgroundColor: rateColor || '#ec4899' }}
                              >
                                +€{rate.fixed_surcharge}
                              </Badge>
                              {ratePrice.baseDiscount > 0 && !rate.isUpcoming && (
                                <Badge className="bg-green-500 text-white text-xs px-2 py-0.5">
                                  -{ratePrice.baseDiscount}%
                                </Badge>
                              )}
                            </>
                          ) : (
                            <>
                              {rate.discount_percent > 0 && (
                                <Badge className="bg-green-500 text-white text-xs px-2 py-0.5">
                                  -{rate.discount_percent}%
                                </Badge>
                              )}
                              {rate.isQualified && !rate.isUpcoming && (
                                <Badge variant="outline" className="border-green-500 text-green-600 text-xs px-2 py-0.5">
                                  ✓ {t('rates.rateApplied')}
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Short Description */}
                        {shortDesc && (
                          <p className={`text-sm mb-2 leading-relaxed ${rate.isUpcoming ? 'text-gray-500' : 'text-gray-600'}`}>
                            {shortDesc}
                          </p>
                        )}
                        
                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                          {!rate.isSurcharge && !isPromotional && (
                            <span className="flex items-center gap-1">
                              <Moon className="w-3.5 h-3.5" />
                              {t('rates.minNights')}: {rate.min_nights}
                              {rate.max_nights && `-${rate.max_nights}`}
                            </span>
                          )}
                          {(rate.isSurcharge || isPromotional) && rate.start_date && rate.end_date && (
                            <span className="flex items-center gap-1" style={{ color: rate.isUpcoming ? rateColor : undefined }}>
                              <Calendar className="w-3.5 h-3.5" />
                              {rate.isUpcoming 
                                ? `${t('rates.availableFrom') || 'Dostupno'}: ${formatDate(rate.start_date)} - ${formatDate(rate.end_date)}`
                                : `${t('rates.validPeriod')}: ${formatDate(rate.start_date)} - ${formatDate(rate.end_date)}`
                              }
                            </span>
                          )}
                          {rate.fixed_surcharge > 0 && !rate.isSurcharge && !isPromotional && (
                            <span className="flex items-center gap-1">
                              <Euro className="w-3.5 h-3.5" />
                              +€{rate.fixed_surcharge} {t('rates.perStay')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right Side on desktop / Bottom on mobile - Price & Button */}
                      <div className="flex flex-col items-start min-[500px]:items-end min-[500px]:justify-center gap-2 min-[500px]:flex-shrink-0">
                        {!rate.isUpcoming && (
                          <div className="text-left min-[500px]:text-right">
                            <div 
                              className="text-xl font-bold leading-tight"
                              style={{ color: isPromotional ? rateColor : 'var(--color-primary)' }}
                            >
                              €{ratePrice.total.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {ratePrice.nights} {ratePrice.nights === 1 ? t('properties.night') : t('properties.nights')}
                            </div>
                          </div>
                        )}

                        {rate.isUpcoming ? (
                          <Button
                            onClick={handleAdjustDates}
                            variant="outline"
                            size="sm"
                            className="w-full min-[500px]:w-auto text-xs px-3 py-2 h-auto"
                            style={{ 
                              borderColor: rateColor || '#6b7280',
                              color: rateColor || '#6b7280'
                            }}
                            data-testid={`adjust-dates-upcoming-${rate.id}`}
                          >
                            <Calendar className="w-3.5 h-3.5 mr-1" />
                            {t('rates.changeDates') || 'Prilagodi datume'}
                          </Button>
                        ) : rate.canBook ? (
                          <Button
                            onClick={() => handleBook(rate)}
                            size="sm"
                            className="w-full min-[500px]:w-auto text-sm px-6 py-2.5 h-auto text-white font-semibold uppercase tracking-wide"
                            style={{ 
                              backgroundColor: isPromotional ? rateColor : 'var(--color-primary)',
                            }}
                            data-testid={`book-rate-${rate.id}`}
                          >
                            {t('rates.book')}
                          </Button>
                        ) : (
                          <Button
                            onClick={handleAdjustDates}
                            variant="outline"
                            size="sm"
                            className="w-full min-[500px]:w-auto border-[var(--color-primary)] text-[var(--color-primary)] text-xs px-3 py-2 h-auto"
                            data-testid={`adjust-dates-${rate.id}`}
                          >
                            <Calendar className="w-3.5 h-3.5 mr-1" />
                            {t('rates.adjustDates')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Info for non-qualifying rates - separate section at bottom */}
                    {!rate.isQualified && !rate.canBook && !rate.isUpcoming && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {t('rates.nightsRequired', { count: rate.min_nights })}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <RoomCalendarPicker
          roomId={room.roomId}
          roomName={room.name}
          initialCheckIn={checkIn}
          initialCheckOut={checkOut}
          onConfirm={(newCheckIn, newCheckOut) => {
            onDatesChange(newCheckIn, newCheckOut, room.roomId);
            setShowDatePicker(false);
          }}
          onCancel={() => setShowDatePicker(false)}
        />
      )}
    </div>
  );
};

export default RoomRatesAccordion;
